import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row, Tab, Tabs } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { AdminTaskStatus, AdminTaskType, cpdPointStatus } from '../../../../constants';
import { adminActions } from '../../../../state/modules/admin';
import { GeneralTable, LoadingButton } from '../../../components';
import AdminTaskData from './partials/admin-task-data';
import { taskOptions } from './partials/taskOptions';


const getOptions = (taskType, status, data) => {
  return fp.flow(
    fp.filter(
      ({ accept }) => accept(taskType, status),
    ),
    fp.map(
      ({ option }) => option(data)
    )
  )(taskOptions)
}

const AutorunToggler = ({ autoRun, toggleAutoRun, status, count }) => {

  if (count) {
    if (status === AdminTaskStatus.completed) {
      return autoRun ?
        <Button className='right' onClick={toggleAutoRun}>Stop Process to System</Button> :
        <Button className='right' onClick={toggleAutoRun}>Process to System</Button>
    }

    if (status === AdminTaskStatus.confirmed) {
      return autoRun ?
        <Button className='right' onClick={toggleAutoRun}>Stop Revert</Button> :
        <Button className='right' onClick={toggleAutoRun}>Revert</Button>
    }

    return autoRun ?
      <Button className='right' onClick={toggleAutoRun}>Stop Import Data</Button> :
      <Button className='right' onClick={toggleAutoRun}>Import Data</Button>
  } else {
    return <div></div>
  }
}

const AdminTaskTable = ({
  tasks,
  autoRun,
  toggleAutoRun,
  runningTasks,
  pendingCount,
  completedCount,
  failedCount,
  confirmedCount,
  onFilterChange,
  loadMore,
  loading,
  hasMoreToLoad,
  filter
}) => {
  const columns = [
    {
      key: 'taskType',
      name: 'Type'
    },
    {
      key: 'status',
      name: 'Status'
    },
    {
      key: 'details',
      name: 'Details'
    },
    // {
    //   key: 'options',
    //   name: 'Options'
    // }
  ]

  const mapRows = fp.map(
    ({ id, name, description, data, taskType, status, result }) => {
      const isRunning = fp.includes(id)(runningTasks)
      return {
        taskType: {
          value: taskType
        },
        status: {
          value: isRunning ? <Spinner name='circle' className='spinner' fadeIn='none' /> : status
        },
        details: {
          value: <div>
            <strong>{`${name}`}</strong>
            <div>{`Description: ${description}`}</div>
            <AdminTaskData taskType={taskType} data={data} />
            <div>{`Result: ${result}`}</div>
          </div>
        },
        options: {
          value: <div>
            {
              getOptions(taskType, status, data)
            }
          </div>
        }
      }
    }
  )

  const filterStatus = (status) => fp.filter(
    (task) => task.status === status
  )

  const pendingRows = fp.flow(
    filterStatus(AdminTaskStatus.pending),
    mapRows
  )(tasks)
  const completedRows = fp.flow(
    filterStatus(AdminTaskStatus.completed),
    mapRows
  )(tasks)
  const failedRows = fp.flow(
    filterStatus(AdminTaskStatus.failed),
    mapRows
  )(tasks)
  const confirmedRows = fp.flow(
    filterStatus(AdminTaskStatus.confirmed),
    mapRows
  )(tasks)

  const handleTabSelect = (status) => {
    const updatedFilter = {
      ...filter,
      status: [status]
    }
    onFilterChange(updatedFilter)
  }

  return <div className='admin-task-table'>
    <Row className='no-gutter'>
      <Col>
        <AutorunToggler autoRun={autoRun} toggleAutoRun={toggleAutoRun} status={filter.status} count={filterStatus(filter.status)(tasks).length} />
        <Tabs id='admin-task-status-tabs' onSelect={handleTabSelect}>
          <Tab key={AdminTaskStatus.pending} eventKey={AdminTaskStatus.pending} title={`${AdminTaskStatus.pending}(${pendingCount})`}>
            <GeneralTable className='admin-task-table' rows={pendingRows} columns={columns} />
          </Tab>
          <Tab key={AdminTaskStatus.completed} eventKey={AdminTaskStatus.completed} title={`Imported(${completedCount})`}>
            <GeneralTable className='admin-task-table' rows={completedRows} columns={columns} />
          </Tab>
          <Tab key={AdminTaskStatus.failed} eventKey={AdminTaskStatus.failed} title={`${AdminTaskStatus.failed}(${failedCount})`}>
            <GeneralTable className='admin-task-table' rows={failedRows} columns={columns} />
          </Tab>
          <Tab key={AdminTaskStatus.confirmed} eventKey={AdminTaskStatus.confirmed} title={`Processed successfully(${confirmedCount})`}>
            <GeneralTable className='admin-task-table' rows={confirmedRows} columns={columns} />
          </Tab>
        </Tabs>
      </Col>
    </Row>
    <Row className='no-gutter'>
      <Col>
        {
          hasMoreToLoad ?
            <LoadingButton text='Load More +' onClick={loadMore} loading={loading} /> :
            null
        }
      </Col>
    </Row>
  </div >
}

const mapStateToProps = (state) => ({
  taskQueryResult: state.admin.adminTasks,
  isUploadingCpd: state.admin.isUploadingCPD,
  loading: state.admin.isFetchingTask
})
const mapDispatchToProps = (dispatch) => ({
  getTasks: bindActionCreators(adminActions.admin.queryAdminTasks, dispatch),
  loadMoreTasks: bindActionCreators(adminActions.admin.loadMoreAdminTasks, dispatch),
  uploadExternalCpd: bindActionCreators(adminActions.admin.uploadExternalCpd, dispatch),
  updateCpdPointStatus: bindActionCreators(adminActions.admin.updateCpdPointStatus, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ taskQueryResult }) => {
      const tasks = taskQueryResult ? taskQueryResult.result : []
      const hits = fp.getOr(0)('hits')(taskQueryResult)
      const pendingCount = fp.getOr(0)('totalPending')(taskQueryResult)
      const completedCount = fp.getOr(0)('totalCompleted')(taskQueryResult)
      const failedCount = fp.getOr(0)('totalFailed')(taskQueryResult)
      const confirmedCount = fp.getOr(0)('totalConfirmed')(taskQueryResult)
      const hasMoreToLoad = tasks.length < hits
      return {
        tasks,
        pendingCount,
        completedCount,
        failedCount,
        confirmedCount,
        hasMoreToLoad
      }
    }
  ),
  withStateHandlers(
    ({ adminTaskGroupId }) => ({
      autoRun: false,
      filter: {
        skip: 0,
        take: 6,
        status: AdminTaskStatus.pending,
        adminTaskGroupIds: adminTaskGroupId
      },
      runningTasks: []
    }),
    {
      toggleAutoRun: ({ autoRun }) => () => ({
        autoRun: !autoRun
      }),
      setRunningTasks: () => (runningTasks) => ({
        runningTasks
      }),
      onFilterChange: ({ filter }) => (updatedFilter) => {
        const {
          status
        } = updatedFilter
        const newFilter = {
          ...filter,
          status: fp.first(status)
        }
        return fp.isEqual(filter)(newFilter) ? {} : { filter: newFilter }
      }
    }
  ),
  withHandlers({
    handleTaskExecution: ({ uploadExternalCpd, updateCpdPointStatus }) => (task) => {
      const {
        id,
        taskType,
        data,
        status,
        result
      } = task

      if (taskType === AdminTaskType.kaplanImport) {
        if (status === AdminTaskStatus.pending) {
          const parsed = JSON.parse(data)
          uploadExternalCpd({
            ...parsed,
            relevantTask: id
          })
        }

        if (status === AdminTaskStatus.completed) {
          const parsed = JSON.parse(result)
          fp.forEach(
            ({ userCpdPointId }) => {
              const userCpdPointIdParsed = parseInt(userCpdPointId, 10)
              if (userCpdPointIdParsed) {
                updateCpdPointStatus(id, userCpdPointIdParsed, cpdPointStatus.approved)
              }
            }
          )(parsed)
        }

        if (status === AdminTaskStatus.confirmed) {
          const parsed = JSON.parse(result)
          fp.forEach(
            ({ userCpdPointId }) => {
              const userCpdPointIdParsed = parseInt(userCpdPointId, 10)
              if (userCpdPointIdParsed) {
                updateCpdPointStatus(id, userCpdPointIdParsed, cpdPointStatus.adminReview)
              }
            }
          )(parsed)
        }
      }
    }
  }),
  withHandlers({
    runTask: ({ runningTasks, setRunningTasks, handleTaskExecution }) => (task) => {
      const updatedRunningTasks = runningTasks.concat([task.id])
      setRunningTasks(updatedRunningTasks)
      handleTaskExecution(task)
    },
    loadMore: ({ loadMoreTasks, filter, tasks }) => () => {
      loadMoreTasks({
        ...filter,
        skip: tasks.length
      })
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        getTasks,
        filter
      } = this.props

      getTasks(filter)
    },
    componentDidUpdate(prevProps) {
      const {
        autoRun,
        tasks,
        runTask,
        runningTasks,
        isUploadingCPD,
        getTasks,
        filter,
        setRunningTasks,
        toggleAutoRun
      } = this.props

      if (autoRun) {
        const runnableStatus = [AdminTaskStatus.pending, AdminTaskStatus.completed, AdminTaskStatus.confirmed]
        const runnableTask = fp.find(
          ({ status, id }) => fp.includes(status)(runnableStatus) && !fp.includes(id)(runningTasks)
        )(tasks)
        if (runnableTask && fp.isEmpty(runningTasks)) {
          runTask(runnableTask)
        }
        if (!runnableTask) {
          toggleAutoRun()
        }
      }

      if (!isUploadingCPD && prevProps.isUploadingCpd) {
        getTasks(filter)
      }

      if (!fp.isEqual(tasks)(prevProps.tasks)) {
        setRunningTasks([])
      }

      if (!fp.isEqual(filter)(prevProps.filter)) {
        getTasks(filter)
      }
    }
  })
)(AdminTaskTable)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/admin-task-portal/admin-task-table.js